import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, Wand2, Check, X, Clock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const presets = [
  "What if I switch to Product Management?", "What if I do an MBA?", "What if I learn AI?",
  "What if I move to Bangalore?", "What if I target a ₹20L salary?", "What if I become a freelancer?",
  "What if I start my own business?",
];

export default function Simulator() {
  const [scenario, setScenario] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const run = async (text) => {
    const s = text || scenario;
    if (!s.trim()) return;
    setScenario(s); setBusy(true); setResult(null);
    try { const { data } = await api.post("/simulator", { scenario: s }); setResult(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/app/career")} className="flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div><h1 className="font-display text-3xl font-extrabold tracking-tight">Career Simulator</h1><p className="mt-1 text-muted-foreground">Explore "What if?" scenarios based on your profile.</p></div>

      <div className="flex flex-wrap gap-2">
        {presets.map((p) => <button key={p} onClick={() => run(p)} data-testid="sim-preset"
          className="rounded-full border border-border bg-secondary px-3 py-1.5 text-xs font-semibold hover:border-indigo-400 hover:text-indigo-600">{p}</button>)}
      </div>

      <div className="flex gap-2">
        <input data-testid="sim-input" value={scenario} onChange={(e) => setScenario(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="What if…?" className="flex-1 rounded-full border border-input bg-card px-4 py-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20" />
        <button onClick={() => run()} disabled={busy} data-testid="sim-run-btn" className="flex h-12 items-center gap-2 rounded-full bg-indigo-600 px-5 font-bold text-white hover:bg-indigo-700 disabled:opacity-60">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Wand2 className="h-5 w-5" />}
        </button>
      </div>

      {busy && <div className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin text-indigo-600" /><p className="mt-2 text-sm">Simulating your scenario…</p></div>}

      {result && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="sim-result">
          <div className="rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-600 p-6 text-white">
            <h2 className="font-display text-xl font-bold">{result.title}</h2>
            <p className="mt-1 text-white/80">{result.summary}</p>
            <div className="mt-3 flex items-center gap-2 text-sm text-white/70"><Clock className="h-4 w-4" /> {result.timeline}</div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5"><h3 className="font-display font-bold text-emerald-800">Benefits</h3><ul className="mt-2 space-y-1.5">{result.benefits?.map((b, i) => <li key={i} className="flex gap-2 text-sm text-emerald-900"><Check className="h-4 w-4 shrink-0 text-emerald-600" />{b}</li>)}</ul></div>
            <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5"><h3 className="font-display font-bold text-amber-800">Challenges</h3><ul className="mt-2 space-y-1.5">{result.challenges?.map((c, i) => <li key={i} className="flex gap-2 text-sm text-amber-900"><X className="h-4 w-4 shrink-0 text-amber-600" />{c}</li>)}</ul></div>
          </div>
          <div className="rounded-3xl border border-border bg-card p-5"><h3 className="font-display font-bold">Skills you'd need</h3><div className="mt-2 flex flex-wrap gap-2">{result.required_skills?.map((s) => <span key={s} className="rounded-full bg-secondary px-3 py-1 text-sm font-semibold">{s}</span>)}</div></div>
          <div className="rounded-3xl border border-indigo-200 bg-indigo-50 p-5"><h3 className="font-display font-bold text-indigo-800">Verdict</h3><p className="mt-1 text-sm text-indigo-900">{result.verdict}</p></div>
          <p className="text-center text-[11px] text-muted-foreground">This is a scenario projection, not a prediction or guarantee.</p>
        </motion.div>
      )}
    </div>
  );
}
